import { VendorOpportunity } from 'modules/vendorOpportunity';
import { Invoice } from '../types';
import { Nullable } from '../../../global';

export interface NetFinanceAmountParams {
  netRate: number;
  salesTax: number;
  totalFees: number;
  downPayment: number;
  equipmentCost: number;
}

export interface InvoiceTotalParams {
  netRate: number;
  salesTax: number;
  totalFees: number;
  equipmentCost: number;
}

export interface NetRateParams {
  tradeOwedBalanceAmount: number;
  tradeAllowanceGrossTradeIn: number;
}

export interface TotalFeesParams {
  freightLaborInstallation: number;
  manufacturerRebateAmount: number;
  invoiceWarrantyAndInsuranceCharges: number;
}

class InvoiceCalculations {
  public netFinanceAmount(params: NetFinanceAmountParams) {
    return params.equipmentCost + params.totalFees + params.salesTax - params.netRate - params.downPayment;
  }

  public invoiceTotal(params: InvoiceTotalParams) {
    return params.equipmentCost + params.totalFees + params.salesTax - params.netRate;
  }

  public netRate(params: NetRateParams) {
    return params.tradeAllowanceGrossTradeIn - params.tradeOwedBalanceAmount;
  }

  public totalFees(params: TotalFeesParams) {
    return (
      params.freightLaborInstallation + params.invoiceWarrantyAndInsuranceCharges - params.manufacturerRebateAmount
    );
  }

  public byFullVO(vo: Nullable<VendorOpportunity>) {
    if (!vo) {
      return {
        netRate: 0,
        totalFees: 0,
        financeAmount: 0,
        downPaymentFlat: 0,
        invoiceTotalValue: 0,
        downPaymentPercent: 0,
      };
    }

    const netRate = this.netRate({
      tradeOwedBalanceAmount: vo?.tradeOwedBalanceAmount ?? 0,
      tradeAllowanceGrossTradeIn: vo?.tradeAllowanceGrossTradeIn ?? 0,
    });

    const totalFees = this.totalFees({
      freightLaborInstallation: vo?.freightLaborInstallation ?? 0,
      manufacturerRebateAmount: vo?.manufacturerRebateAmount ?? 0,
      invoiceWarrantyAndInsuranceCharges: vo?.invoiceWarrantyAndInsuranceCharges ?? 0,
    });

    const invoiceTotalValue = this.invoiceTotal({
      netRate,
      totalFees,
      salesTax: vo?.salesTaxOnInvoice ?? 0,
      equipmentCost: parseFloat(vo?.equipmentCost ?? '0'),
    });

    const financeAmount = this.netFinanceAmount({
      netRate,
      totalFees,
      downPayment: vo?.downPayment ?? 0,
      salesTax: vo?.salesTaxOnInvoice ?? 0,
      equipmentCost: parseFloat(vo?.equipmentCost ?? '0'),
    });

    const downPaymentFlat = vo?.downPayment ?? 0;

    const downPaymentPercent = this.calcDownPaymentPercent(invoiceTotalValue, downPaymentFlat);

    return { netRate, totalFees, invoiceTotalValue, financeAmount, downPaymentFlat, downPaymentPercent };
  }

  public calcDownPaymentPercent(invoiceTotal: number, downPaymentFlat: number) {
    if (!invoiceTotal || isNaN(invoiceTotal) || isNaN(downPaymentFlat)) return 0;

    return (downPaymentFlat / invoiceTotal) * 100;
  }

  public calcDownPaymentFlat(invoiceTotal: number, downPaymentPercent: number) {
    return invoiceTotal * (downPaymentPercent / 100);
  }

  public byInvoice(invoice: Invoice) {
    const {
      downPayment,
      equipmentCost,
      salesTaxOnInvoice,
      tradeOwedBalanceAmount,
      manufacturerRebateAmount,
      freightLaborInstallation,
      tradeAllowanceGrossTradeIn,
      invoiceWarrantyAndInsuranceCharges,
    } = invoice;

    const netTrade = this.netRate({ tradeOwedBalanceAmount, tradeAllowanceGrossTradeIn });

    const totalFees = this.totalFees({
      freightLaborInstallation,
      manufacturerRebateAmount,
      invoiceWarrantyAndInsuranceCharges,
    });

    const invoiceTotal = this.invoiceTotal({
      netRate: netTrade,
      totalFees,
      equipmentCost,
      salesTax: salesTaxOnInvoice,
    });

    const netFinanceAmount = this.netFinanceAmount({
      netRate: netTrade,
      totalFees,
      downPayment,
      equipmentCost,
      salesTax: salesTaxOnInvoice,
    });

    return {
      netTrade,
      totalFees,
      invoiceTotal,
      netFinanceAmount,
      downPaymentFlat: downPayment,
      downPaymentPercent: this.calcDownPaymentPercent(invoiceTotal, downPayment),
    };
  }
}

export const invoiceCalculations = new InvoiceCalculations();
